import { databases, databaseId, appwriteConfig } from '../config/appwrite';
import { Category } from '../types';
import { ID, Query } from 'appwrite';

export class CategoryService {
  // Alle Kategorien abrufen
  static async getAllCategories(): Promise<Category[]> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.categories,
        [Query.orderAsc('name'), Query.limit(100)]
      );

      return response.documents.map((doc: any) => ({
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        platformId: doc.platformId,
        slug: doc.slug,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      throw new Error('Kategorien konnten nicht abgerufen werden');
    }
  }

  // Kategorien nach Plattform abrufen
  static async getCategoriesByPlatform(platformId: string): Promise<Category[]> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.categories,
        [Query.equal('platformId', platformId), Query.orderAsc('name'), Query.limit(100)]
      );

      return response.documents.map((doc: any) => ({
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        platformId: doc.platformId,
        slug: doc.slug,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      throw new Error('Kategorien konnten nicht abgerufen werden');
    }
  }

  // Kategorie nach ID abrufen
  static async getCategoryById(id: string): Promise<Category> {
    try {
      const doc = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.categories,
        id
      );

      return {
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        platformId: doc.platformId,
        slug: doc.slug,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      throw new Error('Kategorie konnte nicht abgerufen werden');
    }
  }

  // Neue Kategorie erstellen
  static async createCategory(categoryData: Partial<Category>): Promise<Category> {
    try {
      const doc = await databases.createDocument(
        databaseId,
        appwriteConfig.collections.categories,
        ID.unique(),
        {
          name: categoryData.name,
          description: categoryData.description,
          platformId: categoryData.platformId,
          // slug wird von Appwrite automatisch generiert oder ist nicht erforderlich
          // isActive wird von Appwrite automatisch auf true gesetzt
        }
      );

      return {
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        platformId: doc.platformId,
        slug: doc.slug,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      throw new Error('Kategorie konnte nicht erstellt werden');
    }
  }

  // Kategorie aktualisieren
  static async updateCategory(id: string, categoryData: Partial<Category>): Promise<Category> {
    try {
      const doc = await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.categories,
        id,
        {
          name: categoryData.name,
          description: categoryData.description,
          platformId: categoryData.platformId,
          // slug wird von Appwrite automatisch verwaltet
          // isActive wird von Appwrite automatisch verwaltet
        }
      );

      return {
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        platformId: doc.platformId,
        slug: doc.slug,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      };
    } catch (error) {
      throw new Error('Kategorie konnte nicht aktualisiert werden');
    }
  }

  // Kategorie löschen
  static async deleteCategory(id: string): Promise<void> {
    try {
      await databases.deleteDocument(
        databaseId,
        appwriteConfig.collections.categories,
        id
      );
    } catch (error) {
      throw new Error('Kategorie konnte nicht gelöscht werden');
    }
  }

  // Aktive Kategorien abrufen
  static async getActiveCategories(): Promise<Category[]> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.categories,
        [Query.orderAsc('name'), Query.limit(100)]
      );

      return response.documents.map((doc: any) => ({
        id: doc.$id,
        name: doc.name,
        description: doc.description,
        platformId: doc.platformId,
        slug: doc.slug,
        isActive: true, // Standardwert
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      throw new Error('Aktive Kategorien konnten nicht abgerufen werden');
    }
  }
}
